import React, { useEffect, useState } from "react";
import { Carousel } from "react-bootstrap";
import { motion } from "framer-motion";
import {
  FaArrowLeft,
  FaArrowRight,
  FaArrowUpRightFromSquare,
} from "react-icons/fa6";
import { NavLink } from "react-router-dom";
import "./categories.css";

const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

/* ------------------ ANIMATIONS ------------------ */

const containerVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 35,
    scale: 0.96,
  },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 160,
      damping: 20,
    },
  },
  hover: {
    y: -8,
    transition: { duration: 0.3 },
  },
};

const imageVariants = {
  hover: {
    scale: 1.09,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

/* ------------------ IMAGE HELPER ------------------ */

const getFirstImage = (product) => {
  if (!product?.images) return "/placeholder.jpg";

  if (Array.isArray(product.images)) {
    return product.images[0] || "/placeholder.jpg";
  }

  if (typeof product.images === "object") {
    const firstKey = Object.keys(product.images)[0];
    const imagesArray = product.images[firstKey];

    if (Array.isArray(imagesArray)) {
      return imagesArray[0] || "/placeholder.jpg";
    }
  }

  return "/placeholder.jpg";
};

/* ------------------ COMPONENT ------------------ */

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);

        const res = await fetch(`${REACT_APP_API_URL}/products/categories`);

        if (!res.ok) {
          throw new Error("Failed to load categories");
        }

        const categoryNames = await res.json();

        if (!Array.isArray(categoryNames)) {
          throw new Error("Invalid categories response");
        }

        const categoriesWithImages = await Promise.all(
          categoryNames.map(async (category) => {
            try {
              const productRes = await fetch(
                `${REACT_APP_API_URL}/products?category=${encodeURIComponent(
                  category,
                )}&limit=1`,
              );

              if (!productRes.ok) {
                throw new Error("Product fetch failed");
              }

              const products = await productRes.json();

              return {
                title: category,
                image: getFirstImage(products?.[0]),
              };
            } catch {
              return {
                title: category,
                image: "/placeholder.jpg",
              };
            }
          }),
        );

        setCategories(categoriesWithImages);
        setError(null);
      } catch (err) {
        console.error(err);
        setError(err.message);

        setCategories([
          { title: "MacBook", image: "/placeholder.jpg" },
          { title: "Mac", image: "/placeholder.jpg" },
          { title: "iPhone", image: "/placeholder.jpg" },
          { title: "iPad", image: "/placeholder.jpg" },
          { title: "Watch", image: "/placeholder.jpg" },
          { title: "Accessories", image: "/placeholder.jpg" },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  /* ------------------ CHUNKS ------------------ */

  const chunkSize = 4;

  const categoryChunks = [];

  for (let i = 0; i < categories.length; i += chunkSize) {
    categoryChunks.push(categories.slice(i, i + chunkSize));
  }

  /* ------------------ LOADING ------------------ */

  if (loading) {
    return (
      <section className="category-section">
        <div className="category-header">
          <span>EXPLORE OUR COLLECTION</span>
          <h2>
            Shop by <strong>Category.</strong>
          </h2>
          <p>Discover the perfect Apple product for your lifestyle.</p>
        </div>

        <div className="category-loading">
          <div className="category-loader"></div>
          <p>Loading categories...</p>
        </div>
      </section>
    );
  }

  /* ------------------ UI ------------------ */

  return (
    <section className="category-section">
      <div className="category-header">
        <div>
          <span className="category-eyebrow">EXPLORE OUR COLLECTION</span>

          <h2>
            Shop by <strong>Category.</strong>
          </h2>

          <p>Discover the perfect Apple product for your lifestyle.</p>
        </div>
      </div>

      {error && categories.length === 0 ? (
        <div className="category-empty">
          <p>Unable to load categories. Please try again later.</p>
        </div>
      ) : categoryChunks.length > 0 ? (
        <Carousel
          indicators={false}
          interval={6000}
          pause="hover"
          touch
          prevIcon={
            <div className="category-arrow">
              <FaArrowLeft />
            </div>
          }
          nextIcon={
            <div className="category-arrow">
              <FaArrowRight />
            </div>
          }
        >
          {categoryChunks.map((chunk, slideIndex) => (
            <Carousel.Item key={slideIndex}>
              <motion.div
                className="category-row"
                variants={containerVariants}
                initial="hidden"
                animate="show"
              >
                {chunk.map((cat, i) => (
                  <motion.div
                    className="category-item"
                    key={`${cat.title}-${i}`}
                    variants={cardVariants}
                    whileHover="hover"
                  >
                    <NavLink
                      to={`/shop?category=${encodeURIComponent(cat.title)}`}
                      className="category-link"
                    >
                      <div className="category-card">
                        <div className="category-card-top">
                          <span className="category-number">
                            {String(slideIndex * chunkSize + i + 1).padStart(
                              2,
                              "0",
                            )}
                          </span>

                          <span className="category-explore">
                            <FaArrowUpRightFromSquare />
                          </span>
                        </div>

                        <motion.div
                          className="category-image-wrap"
                          variants={imageVariants}
                        >
                          <img
                            src={cat.image}
                            alt={cat.title}
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = "/placeholder.jpg";
                            }}
                          />
                        </motion.div>

                        <div className="category-card-bottom">
                          <h3>{cat.title}</h3>
                          <span>Shop Now</span>
                        </div>
                      </div>
                    </NavLink>
                  </motion.div>
                ))}
              </motion.div>
            </Carousel.Item>
          ))}
        </Carousel>
      ) : (
        <div className="category-empty">No categories found.</div>
      )}
    </section>
  );
};

export default Categories;
